// client/src/components/AIList.js
import React, { useEffect, useState, useMemo } from "react";
import {
  fetchAIProfiles,
  toggleAIProfileVisibility,
  createAIProfile,
  fetchProfileSuggestions,
  deleteProfileSuggestion,
} from "../api";
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
  Switch,
  FormControlLabel,
  Link as MuiLink,
  TextField,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Container,
  Card,
  CardContent,
  CardActions,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Divider,
} from "@mui/material";
import { Link } from "react-router-dom";
import { rewriteToCloudFront } from "../images";
import FollowButton from "./FollowButton";
import ProfileAvatar from "./ProfileAvatar";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { formatDistanceToNow } from "date-fns";
import { sanitiseHtml } from "../utils/sanitiseHtml";
import { postBodyStyles } from "../styles/postBody";

function AIList({ isAdmin }) {
  const [profiles, setProfiles] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [newProfile, setNewProfile] = useState({
    name: "",
    prompt: "",
    description: "",
  });
  const [loading, setLoading] = useState(true);

  const [suggestionsOpen, setSuggestionsOpen] = useState(false);
  const [suggestions, setSuggestions] = useState(null); // null = not loaded yet
  const [suggestionsLoading, setSuggestionsLoading] = useState(false);
  const [activeSuggestion, setActiveSuggestion] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);

  // Get admin key from localStorage
  const adminKey = localStorage.getItem("adminKey");

  // Helper function to add admin key to path if it exists
  const getPathWithAdminKey = (path) => {
    if (!adminKey) return path;
    return `${path}?adminKey=${adminKey}`;
  };

  const loadSuggestions = async () => {
    if (suggestions !== null || suggestionsLoading) return; // already loaded or loading
    setSuggestionsLoading(true);
    try {
      const data = await fetchProfileSuggestions();
      setSuggestions(data);
    } catch (err) {
      console.error("Failed to fetch profile suggestions:", err);
      setSuggestions([]);
    } finally {
      setSuggestionsLoading(false);
    }
  };

  const safeParse = (s, fallback = null) => {
    try {
      return s ? JSON.parse(s) : fallback;
    } catch {
      return fallback;
    }
  };

  const isUrl = (v) => {
    try {
      return !!new URL(String(v));
    } catch {
      return false;
    }
  };

  useEffect(() => {
    loadProfiles();
  }, []);

  const loadProfiles = async () => {
    try {
      const data = await fetchAIProfiles();
      setProfiles(data);
    } catch (err) {
      console.error("Failed to fetch AI profiles:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleVisibilityToggle = async (profileId, event) => {
    // Prevent the click from triggering the card's link
    event.preventDefault();
    event.stopPropagation();

    try {
      // Optimistically update the UI
      setProfiles((prev) =>
        prev.map((p) =>
          p.id === profileId ? { ...p, isVisible: !p.isVisible } : p
        )
      );

      // Make the API call
      const updatedProfile = await toggleAIProfileVisibility(profileId);

      // Update with server response
      setProfiles((prev) =>
        prev.map((p) => (p.id === updatedProfile.id ? updatedProfile : p))
      );
    } catch (err) {
      console.error("Failed to toggle visibility:", err);
      // Revert the optimistic update on error
      loadProfiles();
    }
  };

  const handleCreateProfile = async () => {
    try {
      const createdProfile = await createAIProfile(
        newProfile.name,
        newProfile.prompt,
        newProfile.description
      );
      setProfiles((prev) => [createdProfile, ...prev]);
      setOpenDialog(false);
      setNewProfile({ name: "", prompt: "", description: "" });
    } catch (err) {
      console.error("Failed to create AI profile", err);
      alert("Failed to create AI profile. Please try again.");
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  function SuggestionCard({ s, isAdmin, onOpen, onDelete }) {
    const examples = useMemo(() => safeParse(s.sampleJson, []), [s.sampleJson]);
    const createdLabel = s.createdAt
      ? formatDistanceToNow(new Date(s.createdAt), { addSuffix: true })
      : "";

    return (
      <Card
        onClick={onOpen}
        sx={{
          mb: 1.25,
          border: "1px dashed",
          borderColor: "divider",
          backgroundColor: "rgba(255,255,255,0.02)",
          cursor: "pointer",
          transition: "transform 0.15s ease, box-shadow 0.15s ease",
          "&:hover": { transform: "translateY(-1px)", boxShadow: 3 },
        }}
      >
        <CardContent sx={{ pt: 1.5, pb: 1.5 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, flex: 1 }}>
              {s.name || "Untitled profile"}
            </Typography>
            <Chip
              size="small"
              label={createdLabel || "new"}
              variant="outlined"
            />
          </Box>

          {s.contactName && (
            <Typography
              variant="caption"
              sx={{ color: "text.secondary", display: "block", mt: 0.25 }}
            >
              Submitted by <strong>{s.contactName}</strong>
            </Typography>
          )}

          {s.description && (
            <Typography
              sx={{
                mt: 1,
                whiteSpace: "pre-wrap",
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
                opacity: 0.9,
              }}
            >
              {s.description}
            </Typography>
          )}

          <Box sx={{ mt: 1, display: "flex", alignItems: "center", gap: 1 }}>
            <Chip
              size="small"
              variant="filled"
              label={`Example posts: ${examples?.length || 0}`}
            />
            {(s.imageUrl || s.imagePrompt || s.profileImage) && (
              <Chip
                size="small"
                variant="outlined"
                label="Has image idea"
                sx={{ opacity: 0.8 }}
              />
            )}
            {/* admin delete (stops open) */}
            {isAdmin && (
              <Button
                size="small"
                color="error"
                startIcon={<DeleteOutlineIcon />}
                onClick={(e) => {
                  e.stopPropagation();
                  onDelete?.();
                }}
                sx={{ ml: "auto" }}
              >
                Delete
              </Button>
            )}
          </Box>
        </CardContent>
      </Card>
    );
  }

  function SuggestionPreviewDialog({ open, onClose, suggestion }) {
    if (!suggestion) return null;

    const promptIdea = safeParse(suggestion.promptIdea, null);
    const examples = safeParse(suggestion.sampleJson, []);

    return (
      <Dialog
        open={open}
        onClose={onClose}
        fullWidth
        maxWidth="sm"
        scroll="paper"
      >
        <DialogTitle>
          {suggestion.name || "Untitled profile"}
          {suggestion.contactName && (
            <Typography variant="caption" sx={{ ml: 1, opacity: 0.7 }}>
              · suggested by {suggestion.contactName}
            </Typography>
          )}
        </DialogTitle>

        <DialogContent dividers>
          {suggestion.description && (
            <Box sx={{ mb: 2 }}>
              <Typography sx={{ whiteSpace: "pre-wrap" }}>
                {suggestion.description}
              </Typography>
            </Box>
          )}

          {(suggestion.profileImage ||
            suggestion.imagePrompt ||
            suggestion.imageUrl) && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                Profile image idea
              </Typography>
              {isUrl(suggestion.imageUrl) ? (
                <MuiLink
                  href={suggestion.imageUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {suggestion.imageUrl}
                </MuiLink>
              ) : (
                <Typography
                  variant="body2"
                  sx={{ opacity: 0.9, whiteSpace: "pre-wrap" }}
                >
                  {suggestion.profileImage ||
                    suggestion.imagePrompt ||
                    suggestion.imageUrl}
                </Typography>
              )}
            </Box>
          )}

          {(promptIdea ||
            suggestion.promptAdvancedGuidelines ||
            suggestion.promptAdvancedExample) && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                How it should generate posts
              </Typography>
              {promptIdea ? (
                <>
                  {promptIdea.mode && (
                    <Chip
                      size="small"
                      label={
                        promptIdea.mode === "advanced" ? "Advanced" : "Simple"
                      }
                      sx={{ mr: 1, mb: 1 }}
                    />
                  )}
                  {promptIdea.simple && (
                    <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                      {promptIdea.simple}
                    </Typography>
                  )}
                  {promptIdea.advanced && (
                    <Box
                      component="pre"
                      sx={{
                        mt: 1,
                        p: 1,
                        border: 1,
                        borderColor: "divider",
                        borderRadius: 1,
                        fontFamily: "monospace",
                        whiteSpace: "pre-wrap",
                        overflowX: "auto",
                        backgroundColor: "rgba(255,255,255,0.03)",
                      }}
                    >
                      {promptIdea.advanced}
                    </Box>
                  )}
                </>
              ) : (
                <>
                  {suggestion.promptAdvancedGuidelines && (
                    <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                      {suggestion.promptAdvancedGuidelines}
                    </Typography>
                  )}
                  {suggestion.promptAdvancedExample && (
                    <Box
                      component="pre"
                      sx={{
                        mt: 1,
                        p: 1,
                        border: 1,
                        borderColor: "divider",
                        borderRadius: 1,
                        fontFamily: "monospace",
                        whiteSpace: "pre-wrap",
                        overflowX: "auto",
                        backgroundColor: "rgba(255,255,255,0.03)",
                      }}
                    >
                      {suggestion.promptAdvancedExample}
                    </Box>
                  )}
                </>
              )}
            </Box>
          )}

          <Divider sx={{ my: 1.5 }} />

          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Example posts {examples?.length ? `(${examples.length})` : ""}
          </Typography>

          {!examples?.length && (
            <Typography variant="body2" sx={{ opacity: 0.7 }}>
              No example posts provided.
            </Typography>
          )}

          {examples?.map((ex, idx) => {
            const safeHtml = sanitiseHtml(ex.text || "");
            const showImgUrl = ex.imageUrl && isUrl(ex.imageUrl);

            return (
              <Card
                key={idx}
                sx={{ mb: 1.25, backgroundColor: "rgba(255,255,255,0.02)" }}
              >
                <CardContent sx={{ pt: 1.5, pb: 1.25 }}>
                  {(ex.text || "").trim() ? (
                    <Box
                      sx={postBodyStyles}
                      dangerouslySetInnerHTML={{ __html: safeHtml }}
                    />
                  ) : (
                    <Typography variant="body2" sx={{ opacity: 0.7 }}>
                      (no text)
                    </Typography>
                  )}

                  {showImgUrl && (
                    <Box sx={{ mt: 1 }}>
                      <img
                        src={ex.imageUrl}
                        alt=""
                        style={{ maxWidth: "100%", borderRadius: 8 }}
                      />
                    </Box>
                  )}

                  {!showImgUrl && ex.imagePrompt && (
                    <Typography
                      variant="caption"
                      sx={{
                        display: "block",
                        mt: 0.5,
                        fontStyle: "italic",
                        opacity: 0.85,
                      }}
                    >
                      Image prompt: {ex.imagePrompt}
                    </Typography>
                  )}

                  {ex.componentSource && (
                    <Box sx={{ mt: 1 }}>
                      <details>
                        <summary style={{ cursor: "pointer", opacity: 0.9 }}>
                          Show component code (not executed)
                        </summary>
                        <pre
                          style={{
                            marginTop: 8,
                            padding: 12,
                            border: "1px solid rgba(255,255,255,0.15)",
                            borderRadius: 6,
                            overflowX: "auto",
                            background: "rgba(255,255,255,0.03)",
                            fontFamily: "monospace",
                            fontSize: 12.5,
                            whiteSpace: "pre-wrap",
                          }}
                        >
                          {ex.componentSource}
                        </pre>
                      </details>
                    </Box>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </DialogContent>

        <DialogActions>
          <Button onClick={onClose} variant="contained">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  return (
    <Container maxWidth="sm">
      <Box sx={{ mb: 4 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 3,
          }}
        >
          {isAdmin && (
            <Button variant="contained" onClick={() => setOpenDialog(true)}>
              Create New Profile
            </Button>
          )}
        </Box>
        {profiles.map((profile) => (
          <Link
            key={profile.id}
            to={getPathWithAdminKey(`/ai/${profile.id}`)}
            style={{ textDecoration: "none" }}
            onClick={(e) => {
              // If the click is on the switch, prevent navigation
              if (
                e.target.closest(".MuiSwitch-root") ||
                e.target.closest(".MuiFormControlLabel-root")
              ) {
                e.preventDefault();
              }
            }}
          >
            <Card
              sx={{
                mb: 0,
                transition: "transform 0.2s, box-shadow 0.2s",
                "&:hover": {
                  transform: "translateY(-2px)",
                  boxShadow: 3,
                },
                cursor: "pointer",
              }}
            >
              <CardContent>
                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                  <ProfileAvatar profile={profile} />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="h6" component="div" noWrap>
                      {profile.name}
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{
                        display: "-webkit-box",
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden",
                      }}
                    >
                      {profile.description}
                    </Typography>
                  </Box>
                  {isAdmin && (
                    <FormControlLabel
                      control={
                        <Switch
                          checked={profile.isVisible}
                          onClick={(e) => {
                            handleVisibilityToggle(profile.id, e);
                          }}
                        />
                      }
                      label="Visible"
                    />
                  )}
                  {/* everybody sees a follow button */}
                  <FollowButton profileId={profile.id} small />
                </Box>
              </CardContent>
            </Card>
          </Link>
        ))}

        {/* ————————————————————————————————————————————————
           Community suggestions (hidden by default)
           ———————————————————————————————————————————————— */}
        <Accordion
          expanded={suggestionsOpen}
          onChange={(_, exp) => {
            setSuggestionsOpen(exp);
            if (exp) loadSuggestions();
          }}
          sx={{
            mt: 2,
            border: "1px dashed",
            borderColor: "primary.main",
            borderRadius: 1,
            backgroundColor: "transparent",
            "&:before": { display: "none" },
          }}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Typography
                variant="h6"
                sx={{ fontWeight: 700, color: "primary.main" }}
              >
                Community suggestions
              </Typography>
              {Array.isArray(suggestions) && (
                <Chip
                  size="small"
                  label={suggestions.length}
                  sx={{ height: 22, fontSize: "0.75rem" }}
                />
              )}
            </Box>
          </AccordionSummary>
          <AccordionDetails sx={{ pt: 0 }}>
            {suggestionsLoading && (
              <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
                <CircularProgress size={24} />
              </Box>
            )}
            {!suggestionsLoading &&
              Array.isArray(suggestions) &&
              suggestions.length === 0 && (
                <Typography sx={{ color: "text.secondary", py: 2 }}>
                  No suggestions yet.
                </Typography>
              )}
            {!suggestionsLoading &&
              Array.isArray(suggestions) &&
              suggestions.length > 0 && (
                <Box sx={{ display: "flex", flexDirection: "column" }}>
                  {suggestions.map((s) => (
                    <SuggestionCard
                      key={s.id}
                      s={s}
                      isAdmin={isAdmin}
                      onOpen={() => {
                        setActiveSuggestion(s);
                        setPreviewOpen(true);
                      }}
                      onDelete={async () => {
                        if (!window.confirm("Delete this suggestion?")) return;
                        try {
                          await deleteProfileSuggestion(s.id);
                          setSuggestions((prev) =>
                            prev.filter((x) => x.id !== s.id)
                          );
                        } catch (err) {
                          console.error("Delete failed:", err);
                          alert(err.message || "Failed to delete");
                        }
                      }}
                    />
                  ))}
                </Box>
              )}
          </AccordionDetails>
        </Accordion>

        {/* ————————————————————————————————————————
           call‑to‑action for public suggestions        */}
        <Link to="/ai/suggest" style={{ textDecoration: "none" }}>
          <Card
            sx={{
              mt: 2,
              mb: 3,
              px: 2,
              py: 3,
              border: "1px dashed",
              borderColor: "primary.main",
              textAlign: "center",
              "&:hover": { backgroundColor: "rgba(29,155,240,0.05)" },
            }}
          >
            <Typography
              variant="h6"
              sx={{ fontWeight: 700, color: "primary.main" }}
            >
              + Suggest your own profile
            </Typography>
          </Card>
        </Link>
      </Box>

      {profiles.length === 0 && (
        <Typography sx={{ color: "text.secondary" }}>
          No AI profiles found.
        </Typography>
      )}

      <Dialog open={openDialog} onClose={() => setOpenDialog(false)}>
        <DialogTitle>Create New AI Profile</DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 2 }}>
            <TextField
              label="Name"
              value={newProfile.name}
              onChange={(e) =>
                setNewProfile({ ...newProfile, name: e.target.value })
              }
              fullWidth
              required
            />
            <TextField
              label="Prompt"
              value={newProfile.prompt}
              onChange={(e) =>
                setNewProfile({ ...newProfile, prompt: e.target.value })
              }
              fullWidth
              required
              multiline
              rows={3}
            />
            <TextField
              label="Description"
              value={newProfile.description}
              onChange={(e) =>
                setNewProfile({ ...newProfile, description: e.target.value })
              }
              fullWidth
              multiline
              rows={2}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
          <Button
            onClick={handleCreateProfile}
            variant="contained"
            disabled={!newProfile.name || !newProfile.prompt}
          >
            Create
          </Button>
        </DialogActions>
      </Dialog>

      {/* Full preview dialog */}
      <SuggestionPreviewDialog
        open={previewOpen}
        suggestion={activeSuggestion}
        onClose={() => {
          setPreviewOpen(false);
          setActiveSuggestion(null);
        }}
      />
    </Container>
  );
}

export default AIList;
